import { useQuery } from '@tanstack/react-query'
import { api } from '../../api'
import { useAuth } from '../../auth'
import { Term } from '../Term'
import { segmentId, type Route } from '../../route'
import { SignInPanel } from './SignInPanel'
import { SeasonBoard } from './SeasonBoard'
import { SeasonList } from './SeasonList'
import { LeagueIndex } from './LeagueIndex'

/**
 * The fantasy tab.
 *
 * **`#/fantasy` is the board, not a menu.** It used to be a directory: pick a league, then pick a
 * season, then bet — three screens to reach the only page anyone came for, on a site where in
 * practice everybody is in the house league and one season is running. The navigation research says
 * the same thing in general terms: a workspace switcher belongs in the chrome, not on the path, and
 * a default context beats a hierarchy when there is almost always exactly one.
 *
 * So the entry point resolves a default — your league, its live season — and renders the board.
 * Everything deeper still has an explicit address (`#/fantasy/{league}/{season}/{view}`), so a
 * leaderboard is still a link you can paste, and someone in two leagues can still reach both.
 */
export function FantasyPage({ route, go }: {
  route: Route
  go: (...segments: (string | number)[]) => void
}) {
  const { me, loading } = useAuth()

  if (loading) {
    return <p className="py-12 text-center text-sm text-ink-faint">Loading…</p>
  }

  if (me === null) {
    return (
      <div className="space-y-6">
        <Intro />
        <SignInPanel />
      </div>
    )
  }

  const first = route.segments[0]

  // The one non-numeric destination: managing leagues, which almost nobody needs and which
  // therefore sits behind the switcher rather than in front of the board.
  if (first === 'leagues') {
    return <LeagueIndex go={go} />
  }

  const leagueId = segmentId(route.segments, 0)
  const seasonId = segmentId(route.segments, 1)

  if (leagueId !== null && seasonId !== null) {
    return <SeasonBoard leagueId={leagueId} seasonId={seasonId} route={route} go={go} />
  }

  if (leagueId !== null) {
    return <SeasonList leagueId={leagueId} go={go} />
  }

  return <DefaultSeason go={go} />
}

export function Intro() {
  return (
    <div className="max-w-3xl space-y-2">
      <h2 className="text-2xl font-semibold">Fantasy League</h2>
      <p className="text-sm text-ink-soft">
        Play money, real matches. You get ten thousand <Term id="gold" define /> and bet against the
        model&rsquo;s own odds — no margin, no house edge, nothing taken off the top. Beat it if you
        can, and beat your friends while you are at it.
      </p>
    </div>
  )
}

/**
 * Your league and its live season, resolved rather than chosen.
 *
 * Prefers the house league, then whichever season is open — which for almost everybody is the only
 * league and the only season there is. The switcher in the board's header is where the rare case
 * lives.
 */
function DefaultSeason({ go }: { go: (...segments: (string | number)[]) => void }) {
  const leagues = useQuery({ queryKey: ['fantasy', 'mine'], queryFn: api.myFantasyLeagues })

  const league =
    (leagues.data ?? []).find((entry) => entry.isHouse) ?? (leagues.data ?? [])[0] ?? null

  const seasons = useQuery({
    queryKey: ['fantasy', 'seasons', league?.id],
    queryFn: () => api.seasons(league!.id),
    enabled: league !== null,
  })

  if (leagues.isPending || (league !== null && seasons.isPending)) {
    return <p className="py-12 text-center text-sm text-ink-faint">Loading…</p>
  }

  if (league === null) {
    return <LeagueIndex go={go} />
  }

  const open = (seasons.data ?? []).find((season) => season.status === 'open')
  const latest = open ?? (seasons.data ?? [])[0]

  if (latest === undefined) {
    return (
      <div className="space-y-6">
        <Intro />
        <p className="rounded border border-line bg-surface px-4 py-8 text-center text-sm text-ink-soft">
          No season is running in {league.name} yet.
          {league.role === 'manager' ? ' Start one from Leagues.' : ' Sit tight.'}
        </p>
        <button
          type="button"
          onClick={() => go('leagues')}
          className="text-xs text-ink-faint hover:text-ink"
        >
          Leagues and invites →
        </button>
      </div>
    )
  }

  // Rendered in place rather than redirected. The address stays `#/fantasy`, which makes it a
  // durable "take me to the game" link, and every tab inside still navigates to its own explicit
  // one — so back from the leaderboard lands here rather than in a redirect loop.
  return (
    <SeasonBoard
      leagueId={league.id}
      seasonId={latest.id}
      route={{ tab: 'fantasy', segments: [] }}
      go={go}
    />
  )
}
